/**
 * Token Refresh Service
 * Handles automatic token refresh and retry logic for expired JWT tokens
 */

import { API_CONFIG, API_ENDPOINTS } from '../lib/api/config';

export interface TokenRefreshResponse {
  success: boolean;
  data?: {
    token: string;
  };
  error?: string;
}

export interface RefreshTokenData {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

class TokenRefreshService {
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private refreshSubscribers: Array<(success: boolean) => void> = [];
  // Correlation ID for the most recent refresh attempt. Sent to the backend
  // as X-Request-Id (which it echoes back into every one of its own log
  // lines for that request) and exposed so apiClient can log the same ID
  // at the point it decides whether to log the user out — this is what lets
  // you grep backend + frontend logs for one ID and see the full story of
  // a single refresh request end to end.
  private lastRefreshRequestId: string | null = null;

  public getLastRefreshRequestId(): string | null {
    return this.lastRefreshRequestId;
  }

  /**
   * Check if the current token is expired or will expire soon
   */
  private isTokenExpired(token: string, bufferMinutes: number = 5): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = payload.exp;
      
      // Consider token expired if it expires within the buffer time
      return (expirationTime - currentTime) < (bufferMinutes * 60);
    } catch (error) {
      return true; // If we can't parse it, consider it expired
    }
  }

  /**
   * Decode the userId a token was issued for, without verifying the signature
   * (verification happens server-side on every request anyway). Used to tell
   * "another tab refreshed the same session" (safe, ignore) apart from
   * "another tab logged in as a different user" (must force this tab to
   * re-sync) when reacting to cross-tab `storage` events.
   */
  public getTokenUserId(token: string): string | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.userId ?? null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get token expiration time in milliseconds
   */
  public getTokenExpirationTime(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      return 0;
    }
  }

  /**
   * Refresh the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');

    const requestId =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `refresh-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.lastRefreshRequestId = requestId;

    if (!refreshToken) {
      console.error(
        '[TokenRefreshService] No refresh_token in storage — cannot attempt refresh.',
        { requestId }
      );
      return false;
    }

    console.info('[TokenRefreshService] Refresh attempt started', { requestId });

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Backend could not run the session check (DB / connection problem).
        // Not an auth rejection, and does not log the user out.
        if (response.status >= 500 || errorData.code === 'SESSION_CHECK_UNAVAILABLE') {
          console.error(
            '[TokenRefreshService] /auth/refresh could not verify the session — BACKEND/DB PROBLEM, not an auth rejection. The user is NOT being logged out. Root cause is in dbErrorCode:',
            {
              requestId,
              httpStatus: response.status,
              code: errorData.code,
              dbErrorCode: errorData.dbErrorCode ?? '(not reported)',
              error: errorData.error || errorData.message,
            }
          );
          return false;
        }

        console.error(
          '[TokenRefreshService] /auth/refresh failed — this is the exact reason the frontend will log the user out:',
          {
            requestId,
            httpStatus: response.status,
            code: errorData.code,
            error: errorData.error || errorData.message,
          }
        );
        return false;
      }

      const data: TokenRefreshResponse = await response.json();

      if (data.success && data.data?.token) {
        // Update the stored token
        localStorage.setItem('auth_token', data.data.token);

        // Update token expiration time
        const expiresAt = this.getTokenExpirationTime(data.data.token);
        localStorage.setItem('token_expires_at', expiresAt.toString());

        console.info('[TokenRefreshService] Refresh succeeded', { requestId });
        return true;
      } else {
        console.error(
          '[TokenRefreshService] /auth/refresh returned success:false without a hard error status',
          { requestId, data }
        );
        return false;
      }
    } catch (error) {
      console.error(
        '[TokenRefreshService] /auth/refresh threw (network error / request never reached the backend):',
        { requestId, error }
      );
      return false;
    }
  }

  /**
   * Handle token refresh with queue management
   */
  private async handleTokenRefresh(): Promise<boolean> {
    if (this.isRefreshing) {
      // If already refreshing, wait for the current refresh to complete
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }

    this.isRefreshing = true;
    this.refreshPromise = this.refreshAccessToken();

    try {
      const success = await this.refreshPromise;
      
      // Notify all subscribers
      this.refreshSubscribers.forEach(callback => callback(success));
      this.refreshSubscribers = [];
      
      return success;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  async ensureValidToken(): Promise<boolean> {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      return false;
    }

    // Check if token is expired or will expire soon
    if (this.isTokenExpired(token)) {
      return await this.handleTokenRefresh();
    }

    return true;
  }

  /**
   * Force refresh the token
   */
  async forceRefresh(): Promise<boolean> {
    return await this.handleTokenRefresh();
  }

  /**
   * Clear all tokens and user data
   */
  clearTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user');
    // Clear per-user session state so the next login starts fresh
    localStorage.removeItem('onboarding_survey_completed');
    localStorage.removeItem('new_signup');
    localStorage.removeItem('selectedBusinessAccountId');
  }

  /**
   * Store tokens after successful login
   */
  storeTokens(tokenData: RefreshTokenData): void {
    localStorage.setItem('auth_token', tokenData.token);
    localStorage.setItem('refresh_token', tokenData.refreshToken);
    localStorage.setItem('token_expires_at', tokenData.expiresAt.toString());
  }

  /**
   * Get current token info
   */
  getTokenInfo(): { token: string | null; refreshToken: string | null; expiresAt: number | null } {
    const token = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const expiresAtStr = localStorage.getItem('token_expires_at');
    const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null;

    return { token, refreshToken, expiresAt };
  }

  /**
   * Check if user should be logged out due to token issues
   */
  shouldLogout(): boolean {
    const { token, refreshToken } = this.getTokenInfo();
    
    // If no tokens at all, should logout
    if (!token && !refreshToken) {
      return true;
    }

    // If no access token but has refresh token, try to refresh first
    if (!token && refreshToken) {
      return false; // Let the refresh process handle it
    }

    // If has access token, check if it's expired and no refresh token
    if (token && !refreshToken && this.isTokenExpired(token, 0)) {
      return true;
    }

    return false;
  }

  /**
   * Set up automatic token refresh before expiration
   */
  setupAutoRefresh(): void {
    const { token } = this.getTokenInfo();
    
    if (!token) {
      return;
    }

    const expiresAt = this.getTokenExpirationTime(token);
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    
    // Refresh token 5 minutes before expiration
    const refreshTime = Math.max(timeUntilExpiry - (5 * 60 * 1000), 60000); // At least 1 minute from now
    
    if (refreshTime > 0) {
      
      setTimeout(async () => {
        await this.ensureValidToken();
        // Set up the next refresh
        this.setupAutoRefresh();
      }, refreshTime);
    }
  }
}

// Create and export a singleton instance
export const tokenRefreshService = new TokenRefreshService();

// Export the class for testing
export { TokenRefreshService };
